import { ref, computed, Ref, WritableComputedRef } from '@nuxtjs/composition-api';

interface UsePlacesAutocomplete {
  init: (locale: string, input: HTMLInputElement) => void;
  streetNumber: Ref<string>;
  route: Ref<string>;
  city: Ref<string>;
  state: Ref<string>;
  postcode: Ref<string>;
  country: Ref<string>;
  addressFull: WritableComputedRef<string>;
}

export interface Address {
  address1: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
}

export const usePlacesAutocomplete = (initialAddress?: Address): UsePlacesAutocomplete => {
  const [initialStreetNumber, ...rest] = initialAddress?.address1.split(' ') || [];
  const streetNumber = ref(initialStreetNumber || '');
  const route = ref(rest.join(' ') || '');
  const city = ref(initialAddress?.city || '');
  const state = ref(initialAddress?.state || '');
  const postcode = ref(initialAddress?.postcode || '');
  const country = ref(initialAddress?.country || '');

  const addressFull = computed({
    get() {
      return streetNumber.value
        ? route.value
          ? `${streetNumber.value} ${route.value}`
          : streetNumber.value
        : route.value;
    },
    set(newVal: string) {
      const [initialStreetNumber, ...rest] = newVal.split(' ');
      streetNumber.value = initialStreetNumber;
      route.value = rest.join(' ');
    },
  });

  const autoCompleteInstance = ref<google.maps.places.Autocomplete | null>(null);

  const init = (locale: string, input: HTMLInputElement): void => {
    // For Google Places - autocomplete the billing address when typing in the street field
    let options = null;

    // Bound the address autocomplete by lat/lng of the region
    switch (locale) {
      case 'au':
        options = {
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(-55.3228175, 72.2460938),
            new window.google.maps.LatLng(-9.0882278, 168.2249543)
          ),
        };
        break;
      case 'us':
        options = {
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(24.9493, -125.0011),
            new window.google.maps.LatLng(49.5904, -66.9326)
          ),
        };
        break;
      case 'gb':
        options = {
          bounds: new window.google.maps.LatLngBounds(
            new window.google.maps.LatLng(49.674, -14.015517),
            new window.google.maps.LatLng(61.061, 2.0919117)
          ),
        };
        break;
    }

    autoCompleteInstance.value = new window.google.maps.places.Autocomplete(input, options);
    autoCompleteInstance.value.setFields(['address_components']);
    autoCompleteInstance.value.addListener('place_changed', () => {
      if (!autoCompleteInstance.value) {
        console.warn('No autocomplete instance');
        return;
      }
      const place = autoCompleteInstance.value.getPlace();

      if (!place || !place.address_components) {
        return;
      }

      for (let i = 0; i < place.address_components.length; i++) {
        const component = place.address_components[i];
        const addressType = component.types[0];

        switch (addressType) {
          case 'street_number':
            streetNumber.value = component.long_name;
            break;
          case 'route':
            route.value = component.short_name;
            break;
          case 'locality':
            city.value = component.long_name;
            break;
          case 'administrative_area_level_1':
            state.value = component.long_name;
            break;
          case 'postal_code':
            postcode.value = component.long_name;
            break;
          case 'country':
            country.value = component.short_name ? component.short_name.toLowerCase() : '';
            break;
        }
      }
    });
  };

  return {
    init,
    streetNumber,
    route,
    city,
    state,
    postcode,
    country,
    addressFull,
  };
};
